import StripeUtil from '@/utils/stripe'
import config from '@/config/config'
import { products, plans, paidFeatures, enterpriseFeatures, shopifyFeatures, walletOrder, enterpriseWalletOrder } from '@/data/plans'
import { functions } from '@/firebase'
import { upgradedAccountAction, upgradedScreenAction } from '@/utils/logsHelpers'
import firebase from 'firebase/app'
import 'firebase/database'

const db = firebase.database()
StripeUtil.init(config.STRIPE_KEY)

export default {
  namespaced: true,
  state: {
    subscriptionProducts: products,
    subscriptionPlans: plans,
    paidFeatures: paidFeatures,
    enterpriseFeatures: enterpriseFeatures,
    shopifyFeatures: shopifyFeatures,
    productOrder: walletOrder,
    enterpriseProductOrder: enterpriseWalletOrder,
    upgradingFor: null,
    showThankYouScreen: false,

    stripeProducts: [],
    limitsPerPlan: [],
    openCapModal: false,
    selectedPlan: null,
    subscribingInProgress: false,
    upgradeResolution: []
  },
  getters: {
    upgradeResolution (state) {
      return state.upgradeResolution
    },
    getSubscribingInProgress (state) {
      return state.subscribingInProgress
    },
    getSelectedPlan (state) {
      return state.selectedPlan || null
    },
    userReachedProjectsLimit (state, getters, rootState) {
      const user = rootState.auth.user
      const userStats = user.usage_stats
      const planDetails = user.plan_details

      if (parseInt(planDetails.projectsLimit)) {
        return userStats.projects >= parseInt(planDetails.projectsLimit)
      }
      return false
    },
    userReachedEmailsLimit (state, getters, rootState) {
      const user = rootState.auth.user
      const userStats = user.usage_stats
      const planDetails = user.plan_details

      if (parseInt(planDetails.emailsLimit)) {
        return userStats.captured_emails >= parseInt(planDetails.emailsLimit)
      }
      return false
    },
    userReachedQRCodesLimit (state, getters, rootState) {
      const user = rootState.auth.user
      const userStats = user.usage_stats
      const planDetails = user.plan_details

      if (user.enterprise) return false

      if (parseInt(planDetails.qrCodesLimit)) {
        return userStats.qr_codes >= parseInt(planDetails.qrCodesLimit)
      }
      return false
    },
    stripeProducts (state) {
      return state.stripeProducts
    },
    limitsPerPlan (state) {
      return state.limitsPerPlan
    },
    showThankYouScreen (state) {
      return state.showThankYouScreen
    },
    showUpgradeModal (state) {
      return !!state.upgradingFor
    },
    upgradingFor (state) {
      return state.upgradingFor
    },
    subscriptionProducts (state) {
      return state.subscriptionProducts
    },
    subscriptionPlans (state) {
      return state.subscriptionPlans
    },
    paidFeatures (state) {
      return state.paidFeatures
    },
    shopifyFeatures (state) {
      return state.shopifyFeatures
    },
    enterpriseFeatures (state) {
      return state.enterpriseFeatures
    },
    productOrder (state) {
      return state.productOrder
    },
    enterpriseProductOrder (state) {
      return state.enterpriseProductOrder
    },
    productsByStripeId (state) {
      const products = {}
      Object.keys(state.subscriptionProducts).forEach(id => {
        products[state.subscriptionProducts[id].stripeId] = id
      })
      return products
    },
    plansByStripeId (state) {
      const plans = {}
      Object.keys(state.subscriptionPlans).forEach(id => {
        plans[state.subscriptionPlans[id].stripeId] = id
      })
      return plans
    },
    usersSubscription (state, getters) {
      return getters.billingInfo.sub || null
    },
    usersSubscriptionProduct (state, getters, rootState, rootGetters) {
      const enterprise = rootGetters['auth/enterprise']
      if (enterprise) {
        return state.subscriptionProducts.premium
      }
      return state.subscriptionProducts[getters.usersSubscriptionPlan.product || 'free']
    },
    usersSubscriptionPlan (state, getters) {
      if (getters.usersSubscription && getters.usersSubscription.plan) {
        const planId = getters.plansByStripeId[getters.usersSubscription.plan]
        return state.subscriptionPlans[planId] || state.subscriptionPlans.free
      } else return state.subscriptionPlans.free
    },
    usersFeatures (state, getters) {
      return getters.usersSubscriptionProduct.features
    },
    billingInfo (state, getters, rootState, rootGetters) {
      const user = rootGetters['auth/user']
      const enterprise = rootGetters['auth/enterprise']
      if (user && !user.enterprise) {
        return user.billing || {}
      } else if (enterprise) {
        return enterprise.billing || {}
      } else return {}
    },
    paymentMethods (state, getters) {
      return getters.billingInfo.methods
    },
    transactions (state, getters) {
      const transactions = getters.billingInfo.payments || {}
      return Object.values(transactions).sort((a, b) => {
        return b.created - a.created
      })
    },
    userIsEnterprise (state, getters, rootState) {
      return !!(rootState.auth.user && rootState.auth.user.enterprise)
    },
    userOnEnterpriseContract (state, getters, rootState) {
      return !!(rootState.auth.user && rootState.auth.user.enterprise && rootState.auth.enterprise && !rootState.auth.enterprise.noContract)
    },
    userHasPaymentMethod (state, getters) {
      if (getters.userIsEnterprise && getters.userOnEnterpriseContract) return true
      else {
        const methods = getters.billingInfo.methods
        return (methods && Object.keys(methods).length)
      }
    },
    // Flag indicating whether all data required to assess feature permissions is loaded
    userFeaturesLoaded (state, getters, rootState, rootGetters) {
      return !!(
        rootState.auth.user && (
          rootGetters['shopify/isPaidShopifyUser'] ||
          (rootState.auth.user.enterprise && rootState.auth.enterprise && getters.userOnEnterpriseContract) ||
          ((!rootState.auth.user.enterprise || (rootState.auth.user.enterprise && rootState.auth.enterprise && !getters.userOnEnterpriseContract)) && getters.billingInfo)
        )
      )
    },
    userHasAccess: (state, getters, rootState, rootGetters) => (feature) => {
      // Access to premium features defined by membership of an enterprise account
      if (rootGetters['shopify/isPaidShopifyUser']) {
        return getters.shopifyFeatures[feature]
      } else if (getters.userIsEnterprise && getters.userOnEnterpriseContract) {
        if (feature in getters.enterpriseFeatures) {
          return rootGetters['auth/enterprise'].permissions[feature]
        } else {
          return true
        }
        // Or by user's individual subscription plan
      } else if (getters.usersSubscriptionProduct && getters.usersSubscriptionProduct.features) {
        return getters.usersSubscriptionProduct.features[feature]
      } else return false
    }
  },
  mutations: {
    upgradeCompleted (state) {
      state.upgradingFor = null
    },
    upgradeFor (state, feature) {
      state.upgradingFor = feature
    },
    setThankYouScreen (state, status) {
      state.showThankYouScreen = status
    },
    setStripeProducts (state, products) {
      const freePlan = {
        name: 'Free Plan',
        metadata: {
          hasLimits: true,
          appId: 'free'
        }
      }
      state.stripeProducts = [freePlan, ...products]
    },
    setLimitsPerPlan (state, limits) {
      state.limitsPerPlan = limits
    },
    setOpenCapModal (state, status) {
      state.openCapModal = status
    },
    setSelectedPlan (state, plan) {
      state.selectedPlan = plan
    },
    setSubscribingInProgress (state, subscribingInProgress) {
      state.subscribingInProgress = subscribingInProgress
    },
    setUpgradeResolution (state, resolution) {
      state.upgradeResolution = resolution
    }
  },
  actions: {
    updateUpgradeResolution ({ commit }, resolution) {
      commit('setUpgradeResolution', resolution)
    },
    openCapModal ({ commit }, status) {
      commit('setOpenCapModal', status)
    },
    async updateProjectsLimitNotificationStatus ({ }, { userId, status }) {
      const userRef = db.ref(`users/${userId}/plan_details/`)
      await userRef.update({ projectsLimitWarningShown: status })
    },
    async updateEmailsLimitNotificationStatus ({ }, { userId, status }) {
      const userRef = db.ref(`users/${userId}/plan_details/`)
      await userRef.update({ emailsLimitWarningShown: status })
    },
    async updateQRsLimitNotificationStatus ({ }, { userId, status }) {
      const userRef = db.ref(`users/${userId}/plan_details/`)
      await userRef.update({ qrsLimitWarningShown: status })
    },
    async getStripeProducts ({ commit }) {
      const products = await functions.httpsCallable('billing-getStripeProducts')()
      commit('setStripeProducts', products.data.data)
    },
    async getLimitsPerPlan ({ commit }) {
      const limitsRef = db.ref('plans_limits')
      const limitsSnapshot = await limitsRef.once('value')
      const limits = limitsSnapshot.val()
      commit('setLimitsPerPlan', limits)
    },
    async submitPlanLimits ({ dispatch }, { planId, limits }) {
      const limitRef = db.ref(`plans_limits/${planId}`)

      Object.keys(limits).map(limitKey => {
        limits[limitKey] = parseInt(limits[limitKey])
      })

      await limitRef.set(limits)
      dispatch('getLimitsPerPlan')
    },
    // Charge Card
    async chargeForTest ({ getters, rootGetters }, { testId, test, description, paymentMethod }) {
      let activePaymentMethod
      const method = rootGetters.testMethods[test.method]
      let customer = ''

      if (!method.shareMode) {
        // Need a valid billing method to charge for advertising (non-share) tests
        if (!getters.userHasPaymentMethod) {
          throw new Error('No billing method configured')
        }
        if (!getters.userOnEnterpriseContract) {
          activePaymentMethod = paymentMethod || getters.billingInfo.default_method || Object.keys(getters.billingInfo.methods)[0]
          customer = getters.billingInfo.customer
        }
      }
      var data = {
        customer_id: customer,
        payment_method_id: activePaymentMethod,
        description: description,
        testId: testId,
        confirmation_hook: config.TEST_PUBLISHED_WEBHOOK
        // noCampaign: true
      }

      const chargeCall = functions.httpsCallable('billing-chargeForTest')
      var charge = await StripeUtil.charge({ chargeData: data, confirmOnServer: chargeCall })
      return charge
    },
    async subscribeToPlan ({ getters, commit, dispatch, rootState }, payload) {
      // Require a valid Stripe payment method to subscribe to a plan
      if (!getters.userHasPaymentMethod) {
        upgradedAccountAction(rootState.auth.user, { ...payload.plan, error: 'No billing method configured' }, false)
        throw new Error('No billing method configured')
      } else if (getters.userOnEnterpriseContract) {
        // Can't subscribe if on an enterprise contract (because billing handled externally)
        upgradedAccountAction(rootState.auth.user, { ...payload.plan, error: 'Subscriptions not supported on this account' }, false)
        throw new Error('Subscriptions not supported on this account')
      }

      var plan = payload.plan
      var paymentMethod = payload.method || getters.billingInfo.default_method
      var subscription = payload.subscription
      var invoice = payload.invoice

      const subscriptionCall = functions.httpsCallable('billing-subscribeToPlan')

      var result = await subscriptionCall({
        customer: getters.billingInfo.customer,
        method: paymentMethod,
        plan,
        invoice,
        subscription
      })
      var chargeResult = result.data
      if (!chargeResult.confirmed && chargeResult.intent_secret) {
        chargeResult = await StripeUtil.confirmPayment(chargeResult)
      } else if (chargeResult.confirmed) {
        commit('auth/setUserSubscription', plan, { root: true })
        dispatch('logAchievement', { achievement: 'upgrade-plan' }, { root: true })

        commit('pushAnalyticsEvent', {
          action: 'CompletedSubscription',
          properties: {}
        }, { root: true })

        upgradedAccountAction(rootState.auth.user, plan)
      }
      return chargeResult
    },
    async updateCardForPlan ({ getters }, { paymentMethod }) {
      // Require an active subscription
      const sub = getters.billingInfo.sub
      if (sub && sub.id) {
        var result = await functions.httpsCallable('billing-updateCardForPlan')({
          subscription: sub.id,
          method: paymentMethod
        })
        return result
      }
    },
    async retryInvoice ({ getters }, { paymentMethod }) {
      const sub = getters.billingInfo.sub
      if (sub && sub.latest_invoice && sub.latest_invoice.id) {
        var result = await functions.httpsCallable('billing-retryInvoice')({
          subscription: sub.id,
          invoice: sub.latest_invoice.id,
          method: paymentMethod
        })
        return result
      } else throw new Error('No outstanding invoice to retry payment')
    },
    async confirmSCA ({ getters, commit }) {
      const sub = getters.billingInfo.sub
      if (sub &&
          sub.latest_invoice &&
          sub.latest_invoice.id &&
          sub.intent_secret &&
          sub.action_required) {
        var result = await StripeUtil.confirmPayment({
          intent_secret: sub.intent_secret,
          method: sub.method
        })
        if (result.confirmed) {
          commit('auth/setSubscriptionIssueResolved', null, { root: true })
        }
        return result
      } else throw new Error('Unable to retrieve your invoice details for 3D secure authentication')
    },
    async cancelPlan ({ commit, getters, rootState }) {
      const user = rootState.auth.user
      const sub = getters.billingInfo.sub
      if (sub) {
        commit('auth/setSubscriptionCancelling', true, { root: true })
        return functions.httpsCallable('billing-cancelPlan')({ subscription: sub.id, email: user.email })
      }
    },
    async resumePlan ({ commit, getters }) {
      const sub = getters.billingInfo.sub
      if (sub) {
        commit('auth/setSubscriptionCancelling', false, { root: true })
        return functions.httpsCallable('billing-resumePlan')({ subscription: sub.id })
      }
    },
    async switchPlan ({ commit, getters }, planId) {
      const billingInfo = getters.billingInfo
      if (billingInfo.sub) {
        commit('auth/setSubscriptionSwitching', null, { root: true })

        return functions.httpsCallable('billing-switchPlan')({
          subscription: billingInfo.sub.id,
          customer: billingInfo.customer,
          newPlan: planId
        })
      }
    },
    async getPaymentIntentForPaymentRequest ({}, { price, currency }) {
      const clientSecret = await functions.httpsCallable('billing-getPaymentIntentForPaymentRequest')({ price, currency })
      return clientSecret
    },
    async getPaymentMethodIntent () {
      var result = await functions.httpsCallable('billing-saveCardIntent')()
      return result.data
    },
    async addPaymentMethod ({ commit }, { paymentMethod, billingDetails }) {
      await functions.httpsCallable('billing-attachCard')({
        payment_method_id: paymentMethod,
        billing_details: billingDetails
      })
      commit('pushAnalyticsEvent', {
        action: 'AddedCardDetails',
        properties: {}
      }, { root: true })
    },
    async removePaymentMethod ({  }, methodId) {
      await functions.httpsCallable('billing-removeCard')({
        payment_method_id: methodId
      })
    },
    async setDefaultPaymentMethod ({  }, methodId) {
      await functions.httpsCallable('billing-setDefaultCard')({
        payment_method_id: methodId
      })
    },
    async generateChurnkeyHash ({  }, { customerId }) {
      const userHashResponse = await functions.httpsCallable('billing-generateChurnkeyHash')({
        customerId
      })

      return userHashResponse.data.userHash
    },
    async checkForManuallyAddedPlan ({  }, { customer, uid }) {
      const checkResult = await functions.httpsCallable('billing-checkForManuallyAddedSubscription')({
        customer,
        uid
      })
      return checkResult
    },
    enforceFeature ({ commit, rootState, rootGetters }, feature) {
      if (rootState.auth.user.roles && rootState.auth.user.roles.admin) {
        return true
      }

      if (rootGetters['wallet/userHasAccess'](feature)) {
        return true
      } else {
        commit('upgradeFor', feature)

        if (feature !== 'qr-code-studio') {
          commit('pushAnalyticsEvent', {
            action: 'DisplayedUniversalPaywall',
            properties: {
              forFeature: feature
            }
          }, { root: true })
        }

        upgradedScreenAction(rootState.auth.user, { message: `Was asked to upgrade for next feature: ${feature}` })
        return false
      }
    }
  }
}
